import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, Navigate } from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import { CreatorCollectionProvider } from './contexts/CreatorCollectionContext';
import CriticalChangeProvider from './components/common/CriticalChangeProvider';
import RouteErrorBoundary from './components/common/RouteErrorBoundary';
import PasswordOnboardingGate from './components/auth/PasswordOnboardingGate';
import LanguageSwitcherVisibility from './components/system/LanguageSwitcherVisibility';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthRequiredRoute from './components/auth/AuthRequiredRoute';
import GuestOnlyRoute from './components/auth/GuestOnlyRoute';
// Auth pages (includes Index)
const IndexPage = lazy(() => import('./pages/auth/IndexPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const LoginEmailPage = lazy(() => import('./pages/auth/LoginEmailPage'));
const LoginInfluencerEmailPage = lazy(() => import('./pages/auth/LoginInfluencerEmailPage'));
const LineCallbackPage = lazy(() => import('./pages/auth/LineCallbackPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
// Dashboard pages
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
// Campaign pages
const CampaignPage = lazy(() => import('./pages/campaign/CampaignPage'));
/* 시딩 공급가 점검 — 요금표 정가와 저장된 공급가를 대조한다(2026-08-10) */
const SeedingPriceAudit = lazy(() => import('./pages/campaign/SeedingPriceAudit'));
/* 레이트카드 — 대외 카드와 같은 모양의 판매 조건표. 금액은 요금표에서 읽는다(2026-08-13) */
const RateCard = lazy(() => import('./pages/campaign/RateCard'));
const CampaignDetailPage = lazy(() => import('./pages/campaign/CampaignDetailPage'));
const RecruitmentPreviewPage = lazy(() => import('./pages/campaign/RecruitmentPreviewPage'));
const ApplySubmitPage = lazy(() => import('./pages/campaign/ApplySubmitPage'));
// KOLs DB pages
const KolsDBPage = lazy(() => import('./pages/kols-db/KolsDBPage'));
// My KOLs pages
const MyKolsPage = lazy(() => import('./pages/my-kols/MyKolsPage'));
// Delivery pages
const DeliveryPage = lazy(() => import('./pages/delivery/DeliveryPage'));
// Settlement pages (정산 — 받을 돈 · 줄 돈 · 캠페인 수익성)
const SettlementPage = lazy(() => import('./pages/settlement/SettlementPage'));
// Shipping pages
const ShippingPage = lazy(() => import('./pages/shipping/ShippingPage'));
const ShippingDetailPage = lazy(() => import('./pages/shipping/ShippingDetailPage'));
// Influencer pages
const SignupInfluencerPage = lazy(() => import('./pages/influencer/SignupInfluencerPage'));
const MyPage = lazy(() => import('./pages/influencer/MyPage'));
const ConfirmationInProgress = lazy(() => import('./pages/influencer/ConfirmationInProgress'));
const ConfirmationSuccess = lazy(() => import('./pages/influencer/ConfirmationSuccess'));
// Contract pages
const ContractInfluencerPage = lazy(() => import('./pages/contract/ContractInfluencerPage'));
const KolDraftPage = lazy(() => import('./pages/draft/KolDraftPage'));
// 시딩 신청(KOL·신청자용 공개 화면 · 직원용 신청서 관리) — 2026-11-04
const SeedFormPage = lazy(() => import('./pages/seed/SeedFormPage'));
const SeedHubPage = lazy(() => import('./pages/seed/SeedHubPage'));
const PrivacyPage = lazy(() => import('./pages/seed/PrivacyPage'));
const SeedingAdminPage = lazy(() => import('./pages/seeding/SeedingAdminPage'));
const ContractClientPage = lazy(() => import('./pages/contract/ContractClientPage'));
// 회사 초대코드 발급 · 가입 이메일 확인 (MASTER 전용 — 화면 안에서 한 번 더 막는다)
const ClientInvitePage = lazy(() => import('./pages/account/ClientInvitePage'));
const TrashPage = lazy(() => import('./pages/trash/TrashPage'));
const GuidePage = lazy(() => import('./pages/guide/GuidePage'));
// Invoice pages
// 합본 청구서 — 정산 「월별 청구」에서 고른 줄을 한 장으로 묶는다(배송 명세와는 다른 서류다)
const BundleInvoicePage = lazy(() => import('./pages/invoice/BundleInvoicePage'));
/* 받는 쪽이 보는 청구서 — 사이드바도 메뉴도 없다. 링크로 보내거나 인쇄해 PDF 로 준다
   (2026-08-04 대표님: 「그냥 링크로 볼 수 있게 주고 그쪽에서 확정이나 수정 요청 주면」) */
const InvoiceViewPage = lazy(() => import('./pages/invoice/InvoiceViewPage'));
const InvoicePage1 = lazy(() => import('./pages/invoice/InvoicePage1'));
const InvoicePage2 = lazy(() => import('./pages/invoice/InvoicePage2'));
const InvoicePage3 = lazy(() => import('./pages/invoice/InvoicePage3'));

// 같은 라우트(/shipping/:id) 내에서 id만 바뀔 때 ShippingDetailPage가
// unmount되지 않아 이전 쉬핑의 state(addresses 등)가 잔존 → Step 1 등이 중복 표시되는 버그 방지.
// id를 key로 줘서 id 변경 시 페이지 전체를 강제 remount하여 모든 state를 초기화.
function ShippingDetailKeyed() {
  const { id } = useParams();
  return <ShippingDetailPage key={id} />;
}

function App() {
  return (
    <ToastProvider>
    <CriticalChangeProvider>
    <CreatorCollectionProvider>
      <Router>
        {/* 초기 비밀번호(0000)로 로그인한 계정은 비밀번호를 정하기 전에는 아무것도 못 쓴다 */}
        <PasswordOnboardingGate />
        {/* 오른쪽 아래 언어 선택 창은 첫 화면·로그인·회원가입·비밀번호 온보딩에서만 */}
        <LanguageSwitcherVisibility />
        {/* 화면 하나가 죽어도 빈 화면이 되지 않게 — 오류 내용을 복사해 보낼 수 있다(2026-10-09) */}
        <RouteErrorBoundary>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center" role="status">페이지를 불러오는 중...</div>}>
        <Routes>
          <Route path="/" element={<GuestOnlyRoute showCurrentCompany><IndexPage /></GuestOnlyRoute>} />
          <Route path="/login" element={<GuestOnlyRoute><LoginPage /></GuestOnlyRoute>} />
          <Route path="/login-email" element={<GuestOnlyRoute><LoginEmailPage /></GuestOnlyRoute>} />
          <Route path="/login-influencer-email" element={<GuestOnlyRoute><LoginInfluencerEmailPage /></GuestOnlyRoute>} />
          {/* LINE OAuth 콜백 — 백엔드에서 token 쿼리로 리다이렉트 */}
          <Route path="/auth/line/callback" element={<LineCallbackPage />} />
          <Route path="/signup" element={<GuestOnlyRoute><SignupPage /></GuestOnlyRoute>} />
          <Route path="/signup-influencer" element={<GuestOnlyRoute><SignupInfluencerPage /></GuestOnlyRoute>} />
          <Route path="/confirmation/inprogress" element={<ConfirmationInProgress />} />
          <Route path="/confirmation/success" element={<ConfirmationSuccess />} />
          <Route path="/reset-password" element={<GuestOnlyRoute><ResetPasswordPage /></GuestOnlyRoute>} />
          <Route path="/contract/influencer" element={<ContractInfluencerPage />} />
          {/* 로그인 없이 여는 KOL 영상 피드백(보기 전용, 2026-10-30) */}
          <Route path="/kol-draft/:token" element={<KolDraftPage />} />
          {/* 시딩 신청 — 로그인 없이 열린다(신청자는 LINE·Google 별도 로그인). /apply/:id 는 기존 모집 지원 화면이라 따로 둔다 */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/seed" element={<SeedHubPage />} />
          <Route path="/seed/:slug" element={<SeedFormPage />} />
          <Route path="/seeding" element={<ProtectedRoute requiredPermission="canViewCampaign"><SeedingAdminPage /></ProtectedRoute>} />
          <Route path="/contract/client" element={<ContractClientPage />} />
          <Route path="/dashboard" element={<ProtectedRoute requiredPermission="canViewDashboard"><DashboardPage /></ProtectedRoute>} />
          {/* 버블 시절 주소(/dashboard/<회사 id>) 즐겨찾기 — 새 시스템엔 그 자리가 없어 빈 화면이 떴다.
              2026-10-07 태국 KOL팀 «접속이 안 된다»의 원인. 회사는 로그인으로 정해지므로 id 는 버리고 대시보드로 */}
          <Route path="/dashboard/*" element={<Navigate to="/dashboard" replace />} />
          <Route path="/kols-db" element={<ProtectedRoute requiredPermission="canViewKolsDB"><KolsDBPage /></ProtectedRoute>} />
          <Route path="/my-kols" element={<ProtectedRoute requiredPermission="canViewMyKols"><MyKolsPage /></ProtectedRoute>} />
          <Route path="/my-kols/:id" element={<ProtectedRoute requiredPermission="canViewMyKols"><MyKolsPage /></ProtectedRoute>} />
          <Route path="/campaign" element={<ProtectedRoute requiredPermission="canViewCampaign"><CampaignPage /></ProtectedRoute>} />
          {/* 「/campaign/:id」보다 먼저 둔다 — 뒤에 두면 price-audit 이 캠페인 id 로 잡힌다 */}
          <Route path="/campaign/price-audit" element={<ProtectedRoute requiredPermission="canViewCampaign"><SeedingPriceAudit /></ProtectedRoute>} />
          {/* 서비스 단가표 — 메인 메뉴다. 옛 주소(/campaign/rate-card)로 와도 같은 화면을 연다 */}
          <Route path="/rate-card" element={<ProtectedRoute requiredPermission="canViewCampaign"><RateCard /></ProtectedRoute>} />
          <Route path="/campaign/rate-card" element={<ProtectedRoute requiredPermission="canViewCampaign"><RateCard /></ProtectedRoute>} />
          <Route path="/campaign/:id" element={<ProtectedRoute requiredPermission="canViewCampaign"><CampaignDetailPage /></ProtectedRoute>} />
          <Route path="/apply/:id" element={<AuthRequiredRoute><RecruitmentPreviewPage /></AuthRequiredRoute>} />
          <Route path="/apply/:id/submit" element={<AuthRequiredRoute><ApplySubmitPage /></AuthRequiredRoute>} />
          <Route path="/mypage" element={<AuthRequiredRoute><MyPage /></AuthRequiredRoute>} />
          <Route path="/guide" element={<AuthRequiredRoute><GuidePage /></AuthRequiredRoute>} />
          <Route path="/guide/:role" element={<AuthRequiredRoute><GuidePage /></AuthRequiredRoute>} />
          <Route path="/guide/:role/:articleId" element={<AuthRequiredRoute><GuidePage /></AuthRequiredRoute>} />
          <Route path="/delivery" element={<ProtectedRoute requiredPermission="canViewDelivery"><DeliveryPage /></ProtectedRoute>} />
          <Route path="/delivery/:id" element={<ProtectedRoute requiredPermission="canViewDelivery"><DeliveryPage /></ProtectedRoute>} />
          <Route path="/settlement" element={<ProtectedRoute requiredPermission="canViewSettlement"><SettlementPage /></ProtectedRoute>} />
          <Route path="/shipping" element={<ProtectedRoute requiredPermission="canViewShipping"><ShippingPage /></ProtectedRoute>} />
          <Route path="/shipping/:id" element={<ProtectedRoute requiredPermission="canViewShipping"><ShippingDetailKeyed /></ProtectedRoute>} />
          {/* 권한 표 «회사 초대코드 관리»·«휴지통» — 로그인만 보던 것을 권한으로(2026-10-06 권한 재점검 S4: 고객·배송사·KOL 이 주소로 열었다) */}
          <Route path="/client-invites" element={<ProtectedRoute requiredPermission="canManageClientInvites"><ClientInvitePage /></ProtectedRoute>} />
          <Route path="/trash" element={<ProtectedRoute requiredPermission="canViewTrash"><TrashPage /></ProtectedRoute>} />
          {/* 배송 명세는 **정산 › 인보이스 안에** 있다. 예전에는 같은 화면이 사이드바가 붙은
              독립 페이지로도 떠서, 정산 안의 것과 무엇이 다른지 알 수 없었다
              (2026-08-10 대표님: 「이 창은 왜 있어야 되는지 모르겠음」).
              자리를 하나로 합치고, 옛 주소·즐겨찾기는 그리로 넘긴다. */}
          <Route path="/invoices" element={<Navigate to="/settlement?tab=billing&view=delivery" replace />} />
          <Route path="/invoices/bundle" element={<ProtectedRoute requiredPermission="canViewInvoice"><BundleInvoicePage /></ProtectedRoute>} />
          {/* 받는 쪽이 여는 자리 — 우리 메뉴를 거치지 않는다 */}
          <Route path="/invoices/view" element={<InvoiceViewPage />} />
          <Route path="/invoice/:campaignId/:settlementId" element={<ProtectedRoute requiredPermission="canViewInvoice"><InvoicePage1 /></ProtectedRoute>} />
          <Route path="/invoice/:campaignId/:settlementId/form" element={<ProtectedRoute requiredPermission="canViewInvoice"><InvoicePage2 /></ProtectedRoute>} />
          <Route path="/invoice/:campaignId/:settlementId/complete" element={<ProtectedRoute requiredPermission="canViewInvoice"><InvoicePage3 /></ProtectedRoute>} />
          {/* 그 밖의 모르는 주소(버블 옛 주소 등)도 빈 화면 대신 첫 화면으로 — 로그인돼 있으면 거기서 현재 회사·이동 버튼이 보인다 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </Router>
    </CreatorCollectionProvider>
    </CriticalChangeProvider>
    </ToastProvider>
  );
}

export default App;
